import React, { FunctionComponent, useState, useRef, useEffect, useLayoutEffect } from 'react';
import styles from './Step5.module.css';

type RepairEstimateRow = {
  id: string;
  description: string;
  unit: string;
  price: string;
};

interface RepairEstimateData {
  estimateNumber: string;
  paymentMethod: string;
  paymentNumber: string;
  rows: RepairEstimateRow[];
}

type RecommendationSection = {
  title: string;
  rows: RepairEstimateRow[];
};

type EstimatePage = {
  sections: RecommendationSection[];
  showSummary: boolean;
  showNotes: boolean;
};

// Row cells use fontSize 12px with lineHeight 1.4 (~16.8px per wrapped line) plus 8px of
// vertical padding (4px top + 4px bottom) in both the PDF (minHeight, box-sizing: border-box)
// and live-preview (natural auto height) renders. Table/summary/notes positions below a section
// are computed from an *estimate* of this same value, so the estimate must match the real CSS
// exactly or the estimated position drifts away from where the row actually ends on screen/PDF,
// showing up as extra (or missing) whitespace before the next block.
const ROW_LINE_HEIGHT_PX = 16.8;
const ROW_VERTICAL_PADDING_PX = 8;
const estimateRowBoxHeight = (rowSpan: number) => Math.round(ROW_LINE_HEIGHT_PX * rowSpan) + ROW_VERTICAL_PADDING_PX;

interface Step5Part2Props {
  isPDF?: boolean;
  currentEstimatePage?: number;
  repairEstimateData?: RepairEstimateData;
  updateRepairEstimateData?: (data: RepairEstimateData) => void;
  section1?: RecommendationSection; // Keep for backward compatibility
  section2?: RecommendationSection; // Keep for backward compatibility
  sections?: RecommendationSection[]; // New: array of sections
  notes?: string;
  dataSourceUrl?: string;
  // Whether the combined totals table (only relevant when there's more than one
  // section) is included. Defaults to true so existing callers/reports are unaffected.
  includeSummaryTable?: boolean;
}

const Step5Part2: FunctionComponent<Step5Part2Props> = ({
  isPDF = false,
  currentEstimatePage = 1,
  repairEstimateData = {
    estimateNumber: '',
    paymentMethod: '',
    paymentNumber: '',
    rows: []
  },
  updateRepairEstimateData,
  section1,
  section2,
  sections, // New prop for array of sections
  notes,
  dataSourceUrl,
  includeSummaryTable = true
}) => {
  const [localData, setLocalData] = useState<RepairEstimateData>(repairEstimateData);
  const tableRef = useRef<HTMLDivElement>(null);
  const pageContainerRef = useRef<HTMLDivElement>(null);
  // Real rendered bottom edge (in the same px coordinate space as the `top` styles below) of the
  // last section table on this page. Row-height estimates drift from the browser's actual text
  // wrapping, so once we have a real measurement we use it to position the summary/notes instead,
  // and only fall back to the estimate before the first measurement is available.
  const lastSectionTableRef = useRef<HTMLDivElement>(null);
  const [measuredLastSectionBottom, setMeasuredLastSectionBottom] = useState<number | null>(null);

  // State for dragging tables (preview mode only)
  const [draggingTableId, setDraggingTableId] = useState<string | null>(null);
  const [dragStart, setDragStart] = useState({ y: 0 });
  const [customPositions, setCustomPositions] = useState<Record<string, number>>({});

  // Sync local state with prop changes
  useEffect(() => {
    setLocalData(repairEstimateData);
  }, [repairEstimateData]);

  // Build sections array: use sections prop if provided, otherwise fall back to section1/section2 for backward compatibility
  const allSections: RecommendationSection[] = sections && sections.length > 0
    ? sections
    : (() => {
      const effectiveSection1: RecommendationSection = {
        title: section1?.title ?? 'Repair Estimate#1',
        rows: (section1?.rows && section1.rows.length > 0)
          ? section1.rows
          : (repairEstimateData?.rows || [])
      };
      const sectionsArray: RecommendationSection[] = [effectiveSection1];
      if (section2 && section2.rows && section2.rows.length > 0) {
        sectionsArray.push(section2);
      }
      return sectionsArray;
    })();

  // For backward compatibility, keep effectiveSection1 and effectiveSection2
  const effectiveSection1: RecommendationSection = allSections[0] || {
    title: 'Repair Estimate#1',
    rows: repairEstimateData?.rows || []
  };
  const effectiveSection2: RecommendationSection | undefined = allSections.length > 1 ? allSections[1] : undefined;

  // Calculate dynamic table heights (still needed for rendering, but not for pagination)
  const calculateTableHeight = (rows: RepairEstimateRow[]) => {
    if (rows.length === 0) return 0;
    const headerHeight = 20; // Header row height
    let dataRowsHeight = 0;

    // Calculate height for each row based on content
    rows.forEach(row => {
      // Calculate how many rows are needed based on content length
      const calculateRowsNeeded = (text: string, baseLength: number = 50) => {
        if (!text || text.trim().length === 0) return 1;

        const lines = text.split('\n').length;
        const wordCount = text.split(' ').length;
        const charCount = text.length;

        // Priority 1: Explicit line breaks (from Enter key) take highest priority
        // Each line break creates a new visual line
        let rowsNeeded = lines;

        // Priority 2: For URLs or long strings without spaces, use character count
        // URLs typically wrap at around 40-45 chars in a narrow column
        const isURL = text.includes('http://') || text.includes('https://') || text.includes('www.');
        const effectiveBaseLength = isURL ? 40 : baseLength;

        // For each line, calculate if it needs to wrap
        const textLines = text.split('\n');
        let totalCharRows = 0;
        textLines.forEach(line => {
          const lineCharRows = Math.ceil(line.length / effectiveBaseLength);
          totalCharRows += Math.max(1, lineCharRows);
        });

        // Use the maximum of explicit lines or character-based wrapping
        rowsNeeded = Math.max(rowsNeeded, totalCharRows);

        // Priority 3: For text with spaces, also consider word count (but only if no explicit line breaks)
        if (lines === 1 && wordCount > 1) {
          const wordRows = Math.ceil(wordCount / 8);
          rowsNeeded = Math.max(rowsNeeded, wordRows);
        }

        // Ensure minimum of 1 row
        return Math.max(rowsNeeded, 1);
      };

      const descriptionRows = calculateRowsNeeded(row.description);
      const unitRows = calculateRowsNeeded(row.unit, 10);
      const priceRows = calculateRowsNeeded(row.price, 10);

      const maxRowsNeeded = Math.max(descriptionRows, unitRows, priceRows);
      // Must match the row's actual rendered height (see descriptionHeight in the section table below).
      const rowHeight = estimateRowBoxHeight(maxRowsNeeded);
      dataRowsHeight += rowHeight;
    });

    // Include total row height (20px) if there are rows
    const totalRowHeight = rows.length > 0 ? 20 : 0;
    return headerHeight + dataRowsHeight + totalRowHeight;
  };

  // Build deterministic pagination structure
  const buildEstimatePages = (sections: RecommendationSection[], hasNotes: boolean, includeSummary: boolean): EstimatePage[] => {
    const SECTIONS_PER_PAGE = 3;
    const MAX_PAGE_HEIGHT = 700; // Maximum usable height per page (790px total - margins)
    const HEADER_HEIGHT = 150; // Height reserved for page header
    const GAP_BETWEEN_SECTIONS = 20; // Gap between sections
    const SUMMARY_TITLE_HEIGHT = 30;
    const NOTES_TITLE_HEIGHT = 25;
    const NOTES_ESTIMATED_HEIGHT = 100; // Estimated height for notes section
    
    const pages: EstimatePage[] = [];
    const totalSections = sections.length;
    const hasMultipleSections = totalSections > 1;
    const showSummaryTable = hasMultipleSections && includeSummary;

    if (totalSections === 0) {
      // No sections - just notes page if notes exist
      if (hasNotes) {
        pages.push({
          sections: [],
          showSummary: false,
          showNotes: true
        });
      }
      return pages;
    }

    // Pre-calculate heights for all sections
    const sectionHeightsArray = sections.map(section => {
      const titleHeight = hasMultipleSections ? 30 : 20; // Title height if showing section names
      const tableHeight = calculateTableHeight(section.rows);
      return titleHeight + tableHeight;
    });

    // Calculate summary table height (only shown if multiple sections and the toggle is on)
    const summaryTableHeight = showSummaryTable
      ? SUMMARY_TITLE_HEIGHT + 20 + (totalSections * 20) + 20 // title + header + rows + total row
      : 0;

    // Helper function to check if content fits on page
    const calculateContentHeight = (sectionIndices: number[], includeSummary: boolean, includeNotes: boolean) => {
      let height = HEADER_HEIGHT;
      
      // Add section heights with gaps
      sectionIndices.forEach((idx, i) => {
        height += sectionHeightsArray[idx];
        if (i < sectionIndices.length - 1) {
          const section = sections[idx];
          const extraSpacing = Math.min(section.rows.length * 2, 30);
          height += GAP_BETWEEN_SECTIONS + extraSpacing;
        }
      });

      // Add gap before summary if there are sections
      if (includeSummary && sectionIndices.length > 0) {
        height += GAP_BEFORE_SUMMARY;
      }

      // Add summary height
      if (includeSummary) {
        height += summaryTableHeight;
      }

      // Add gap before notes if there's summary or sections
      if (includeNotes && (includeSummary || sectionIndices.length > 0)) {
        const extraGap = totalSections > 9 ? 40 : 25;
        height += extraGap;
      }

      // Add notes height
      if (includeNotes) {
        height += NOTES_ESTIMATED_HEIGHT;
      }

      return height;
    };

    // Build pages dynamically based on content height
    let currentPageSections: number[] = [];
    let sectionIndex = 0;

    while (sectionIndex < totalSections) {
      // Try adding sections to current page
      while (sectionIndex < totalSections) {
        const testSections = [...currentPageSections, sectionIndex];
        const heightWithoutSummary = calculateContentHeight(testSections, false, false);

        // Check if we can fit this section
        if (heightWithoutSummary <= MAX_PAGE_HEIGHT) {
          currentPageSections.push(sectionIndex);
          sectionIndex++;
        } else {
          // Can't fit this section
          // If currentPageSections is empty, we MUST add this section anyway (too large but unavoidable)
          if (currentPageSections.length === 0) {
            currentPageSections.push(sectionIndex);
            sectionIndex++;
          }
          // Break to create new page with current sections
          break;
        }

        // Don't exceed sections per page limit
        if (currentPageSections.length >= SECTIONS_PER_PAGE) {
          break;
        }
      }

      // Safety check: if we have no sections but haven't processed all, something went wrong
      if (currentPageSections.length === 0) {
        console.error('No sections added to page, breaking to avoid infinite loop');
        break;
      }

      // Now we have sections for current page, decide about summary/notes
      const isLastSectionBatch = sectionIndex >= totalSections;
      
      if (isLastSectionBatch && showSummaryTable) {
        // Try to fit summary and notes on this page
        const heightWithSummary = calculateContentHeight(currentPageSections, true, false);
        const heightWithBoth = calculateContentHeight(currentPageSections, true, hasNotes);

        if (heightWithBoth <= MAX_PAGE_HEIGHT) {
          // Everything fits!
          pages.push({
            sections: currentPageSections.map(idx => sections[idx]),
            showSummary: true,
            showNotes: hasNotes
          });
        } else if (heightWithSummary <= MAX_PAGE_HEIGHT) {
          // Summary fits, but not notes
          pages.push({
            sections: currentPageSections.map(idx => sections[idx]),
            showSummary: true,
            showNotes: false
          });
          // Add notes-only page
          if (hasNotes) {
            pages.push({
              sections: [],
              showSummary: false,
              showNotes: true
            });
          }
        } else {
          // Summary doesn't fit, create separate page for summary+notes
          pages.push({
            sections: currentPageSections.map(idx => sections[idx]),
            showSummary: false,
            showNotes: false
          });
          pages.push({
            sections: [],
            showSummary: true,
            showNotes: hasNotes
          });
        }
      } else {
        // Not the last batch, or the summary table isn't part of this page (single
        // section, or multiple sections with the summary toggled off) - notes (if
        // any) still need to be placed here rather than silently dropped.
        const shouldShowNotes = !showSummaryTable && isLastSectionBatch && hasNotes;

        if (shouldShowNotes) {
          // For single section with notes, check if they fit together
          const heightWithNotes = calculateContentHeight(currentPageSections, false, true);
          const heightWithoutNotes = calculateContentHeight(currentPageSections, false, false);
          
          // If the section alone is already over the limit, notes definitely need separate page
          if (heightWithoutNotes > MAX_PAGE_HEIGHT || heightWithNotes > MAX_PAGE_HEIGHT) {
            // Section too large or doesn't fit with notes - put notes on separate page
            pages.push({
              sections: currentPageSections.map(idx => sections[idx]),
              showSummary: false,
              showNotes: false
            });
            pages.push({
              sections: [],
              showSummary: false,
              showNotes: true
            });
          } else {
            // Both fit together
            pages.push({
              sections: currentPageSections.map(idx => sections[idx]),
              showSummary: false,
              showNotes: true
            });
          }
        } else {
          // No notes or not applicable - just add sections
          pages.push({
            sections: currentPageSections.map(idx => sections[idx]),
            showSummary: false,
            showNotes: false
          });
        }
      }

      currentPageSections = [];
    }

    // Debug logging
    console.log('=== Estimate Pages Debug ===');
    console.log('Total sections:', totalSections);
    console.log('Has notes:', hasNotes);
    console.log('Pages created:', pages.length);
    pages.forEach((page, idx) => {
      console.log(`Page ${idx + 1}:`, {
        sectionCount: page.sections.length,
        sectionTitles: page.sections.map(s => s.title),
        showSummary: page.showSummary,
        showNotes: page.showNotes
      });
    });

    return pages;
  };

  // Heights are calculated in sectionHeights array above

  // Calculate dynamic Total column width based on maximum total value for a section
  const calculateTotalColumnWidth = (rows: RepairEstimateRow[]) => {
    const minWidth = 60; // Minimum width for small numbers
    const charWidth = 8; // Approximate width per character (including decimal point)
    const padding = 16; // Total padding (8px on each side)

    if (rows.length === 0) return minWidth;

    // Find the maximum total value
    let maxTotal = 0;
    rows.forEach(row => {
      const unitPrice = parseFloat(row.unit) || 0;
      const price = parseFloat(row.price) || 0;
      const total = unitPrice * price;
      if (total > maxTotal) maxTotal = total;
    });

    // Calculate section total
    const sectionTotal = rows.reduce((sum, row) => {
      const unitPrice = parseFloat(row.unit) || 0;
      const price = parseFloat(row.price) || 0;
      return sum + (unitPrice * price);
    }, 0);

    // Use the larger of max row total or section total
    const largestValue = Math.max(maxTotal, sectionTotal);

    // Format the number and calculate width needed
    const formattedValue = largestValue.toFixed(2);
    const valueWidth = formattedValue.length * charWidth + padding;

    // Return the larger of calculated width or minimum width
    return Math.max(valueWidth, minWidth);
  };

  // Calculate Total column width for summary table (combines all sections)
  const calculateSummaryTotalColumnWidth = () => {
    const minWidth = 60;
    const charWidth = 8;
    const padding = 16;

    // Calculate grand total for all sections
    const grandTotal = allSections.reduce((sum, section) => {
      return sum + (section.rows || []).reduce((sectionSum, row) => {
        const unitPrice = parseFloat(row.unit) || 0;
        const price = parseFloat(row.price) || 0;
        return sectionSum + (unitPrice * price);
      }, 0);
    }, 0);

    // Format the number and calculate width needed
    const formattedValue = grandTotal.toFixed(2);
    const valueWidth = formattedValue.length * charWidth + padding;

    // Return the larger of calculated width or minimum width
    return Math.max(valueWidth, minWidth);
  };

  // Calculate widths and heights for all sections
  const sectionHeights = allSections.map(section => calculateTableHeight(section.rows));
  const sectionTotalWidths = allSections.map(section => calculateTotalColumnWidth(section.rows));
  const sectionPriceWidths = sectionTotalWidths.map(width => Math.max(60, Math.ceil(width / 2)));

  // For backward compatibility
  const section1TotalWidth = sectionTotalWidths[0] || 60;
  const section2TotalWidth = sectionTotalWidths[1] || 60;
  const section1PriceWidth = sectionPriceWidths[0] || 60;
  const section2PriceWidth = sectionPriceWidths[1] || 60;
  const section1Height = sectionHeights[0] || 0;
  const section2Height = sectionHeights[1] || 0;

  const summaryTotalWidth = calculateSummaryTotalColumnWidth();
  const summaryPriceWidth = Math.max(60, Math.ceil(summaryTotalWidth / 2));
  const GAP_BETWEEN_TABLES = 20; // Base gap between section tables (reduced by 60%)
  const GAP_BEFORE_SUMMARY = Math.round(GAP_BETWEEN_TABLES * 0.8); // 20% tighter gap before summary block

  // Build pages and get current page info
  const estimatePages = buildEstimatePages(allSections, !!notes, includeSummaryTable);
  const currentPage = estimatePages[currentEstimatePage - 1] || estimatePages[0] || { sections: [], showSummary: false, showNotes: false };
  const sectionsOnCurrentPage = currentPage.sections;
  const showSummaryOnCurrentPage = currentPage.showSummary;
  const showNotesOnCurrentPage = currentPage.showNotes;

  // Debug current page
  console.log('=== Current Page Debug ===');
  console.log('currentEstimatePage:', currentEstimatePage);
  console.log('Total pages available:', estimatePages.length);
  console.log('Current page data:', {
    sectionsCount: sectionsOnCurrentPage.length,
    showSummary: showSummaryOnCurrentPage,
    showNotes: showNotesOnCurrentPage
  });
  console.log('Has notes prop:', !!notes);

  // Calculate positions for all sections dynamically
  // Note: Positions are calculated globally, but pagination will reset positions per page
  const calculateSectionPositions = () => {
    const positions: Array<{ top: number; tableTop: number }> = [];
    let currentTop = 150;

    allSections.forEach((section, index) => {
      const showTitle = allSections.length > 1; // Show title if more than one section
      const sectionTop = currentTop;
      const sectionTableTop = showTitle ? sectionTop + 30 : sectionTop + 20; // 30px gap for title, 20px if no title

      positions.push({ top: sectionTop, tableTop: sectionTableTop });

      // Calculate next section position
      if (index < allSections.length - 1) {
        const extraSpacing = Math.min(section.rows.length * 2, 30);
        currentTop = sectionTableTop + sectionHeights[index] + GAP_BETWEEN_TABLES + extraSpacing;
      }
    });

    return positions;
  };

  const sectionPositions = calculateSectionPositions();

  // For backward compatibility (used in legacy rendering)
  const section1Top = sectionPositions[0]?.top || 150;
  const section1TableTop = sectionPositions[0]?.tableTop || (allSections.length > 1 ? 180 : 170);
  const section2Top = sectionPositions[1]?.top || 150;
  const section2TableTop = sectionPositions[1]?.tableTop || 180;

  // Calculate positions for sections on current page (for rendering only)
  const currentPageSectionPositions: Array<{
    section: RecommendationSection;
    position: { top: number; tableTop: number };
    height: number;
    totalWidth: number;
    priceWidth: number;
    tableId: string;
  }> = [];

  const showTitle = allSections.length > 1;
  let currentPageTop = 150; // Start from top of page

  sectionsOnCurrentPage.forEach((section, localIndex) => {
    // Find global index of this section
    const globalIndex = allSections.findIndex(s => s === section);
    const tableId = `section-${globalIndex}`;

    // Use custom position if available (preview mode only), otherwise use calculated position
    // Custom position stores the table top position (not section top)
    const customTableTop = !isPDF && customPositions[tableId] !== undefined ? customPositions[tableId] : null;
    let sectionTop = currentPageTop;
    let sectionTableTop = showTitle ? sectionTop + 30 : sectionTop + 20;

    if (customTableTop !== null) {
      // Custom position is for table top, calculate section top from it
      sectionTableTop = customTableTop;
      sectionTop = showTitle ? sectionTableTop - 30 : sectionTableTop - 20;
    }

    currentPageSectionPositions.push({
      section,
      position: { top: sectionTop, tableTop: sectionTableTop },
      height: sectionHeights[globalIndex] || 0,
      totalWidth: sectionTotalWidths[globalIndex] || 60,
      priceWidth: sectionPriceWidths[globalIndex] || 60,
      tableId
    });

    // Calculate next section position on this page (if there is one)
    if (localIndex < sectionsOnCurrentPage.length - 1) {
      const extraSpacing = Math.min(section.rows.length * 2, 30);
      // If using custom position, calculate next position from this table's bottom
      // Otherwise use calculated position
      currentPageTop = sectionTableTop + (sectionHeights[globalIndex] || 0) + GAP_BETWEEN_TABLES + extraSpacing;
    }
  });

  // Calculate positions for summary and notes (for rendering only)
  // Calculate actual summary table height based on number of sections
  // Summary table has: header (20px) + one row per section (20px each) + grand total row (20px)
  // Adding extra buffer for preview mode where flex display might add slight extra height
  const actualSummaryTableHeight = allSections.length > 1
    ? 20 + (allSections.length * 20) + 20 + (isPDF ? 0 : Math.max(0, allSections.length - 5) * 2) // header + section rows + grand total + buffer for many sections
    : 0;

  const summaryTableId = 'summary-table';
  const notesTableId = 'notes-section';

  let summaryTop = 0;
  let summaryTableTop = 0;
  let notesTop = 0;

  if (showSummaryOnCurrentPage || showNotesOnCurrentPage) {
    if (sectionsOnCurrentPage.length > 0) {
      // Summary/notes below last section on this page. Prefer the real measured bottom edge
      // (see lastSectionTableRef) over the estimate, since the estimate only approximates how
      // the browser will actually wrap the description text.
      const lastPos = currentPageSectionPositions[currentPageSectionPositions.length - 1];
      const lastSectionBottom = measuredLastSectionBottom ?? (lastPos.position.tableTop + lastPos.height);
      // Use custom position if available (preview mode only)
      const customSummaryTop = !isPDF && customPositions[summaryTableId] !== undefined
        ? customPositions[summaryTableId]
        : lastSectionBottom + GAP_BEFORE_SUMMARY;
      summaryTop = customSummaryTop;
    } else {
      // Summary/notes only page (no sections)
      const customSummaryTop = !isPDF && customPositions[summaryTableId] !== undefined
        ? customPositions[summaryTableId]
        : 150;
      summaryTop = customSummaryTop;
    }
    summaryTableTop = summaryTop + 30; // 30px gap for summary title

    // Use custom notes position if available
    const customNotesTop = !isPDF && customPositions[notesTableId] !== undefined
      ? customPositions[notesTableId]
      : null;

    notesTop = customNotesTop !== null
      ? customNotesTop
      : (showSummaryOnCurrentPage
        ? summaryTableTop + actualSummaryTableHeight + (allSections.length > 9 ? 40 : 25) // Below summary (using actual height, more space for many sections)
        : summaryTop); // At top if no summary
  }

  const isSummaryOnlyPage = sectionsOnCurrentPage.length === 0 && (showSummaryOnCurrentPage || showNotesOnCurrentPage);

  // Debug rendering conditions
  console.log('=== Notes Rendering Conditions ===');
  console.log('notes prop exists:', !!notes);
  console.log('notes prop value:', notes);
  console.log('showNotesOnCurrentPage:', showNotesOnCurrentPage);
  console.log('isSummaryOnlyPage:', isSummaryOnlyPage);
  console.log('showSummaryOnCurrentPage:', showSummaryOnCurrentPage);
  console.log('sectionsOnCurrentPage.length:', sectionsOnCurrentPage.length);
  console.log('Condition 1 (with summary on sections page):', notes && showNotesOnCurrentPage);
  console.log('Condition 2 (with summary on separate page):', notes && showNotesOnCurrentPage && isSummaryOnlyPage);
  console.log('Condition 3 (notes without summary on page, incl. notes-only page):', notes && !showSummaryOnCurrentPage && showNotesOnCurrentPage);

  // Drag handlers for tables (preview mode only)
  const handleTableMouseDown = (e: React.MouseEvent, tableId: string, currentTop: number) => {
    if (isPDF) return;
    e.preventDefault();
    e.stopPropagation();
    setDraggingTableId(tableId);
    const rect = pageContainerRef.current?.getBoundingClientRect();
    if (rect) {
      setDragStart({
        y: e.clientY - rect.top - currentTop
      });
    }
  };

  // Global mouse move handler for dragging tables
  useEffect(() => {
    if (!draggingTableId || isPDF) return;

    const handleMouseMove = (e: MouseEvent) => {
      if (!pageContainerRef.current || !draggingTableId) return;
      const rect = pageContainerRef.current.getBoundingClientRect();

      const newTop = e.clientY - rect.top - dragStart.y;

      // Constrain to page bounds - allow more flexibility for dynamic content
      const minTop = 100; // Below header
      const maxTop = 1500; // Increased to allow for more content

      const constrainedTop = Math.max(minTop, Math.min(maxTop, newTop));

      setCustomPositions(prev => ({
        ...prev,
        [draggingTableId]: constrainedTop
      }));
    };

    const handleMouseUp = () => {
      if (draggingTableId) {
        setDraggingTableId(null);
      }
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [draggingTableId, dragStart, isPDF]);

  // Measure the real bottom edge of the last section table after layout (see lastSectionTableRef
  // above). Intentionally no dependency array so it re-measures after any content/layout change;
  // the equality check prevents a re-render loop.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useLayoutEffect(() => {
    if (lastSectionTableRef.current) {
      const bottom = lastSectionTableRef.current.offsetTop + lastSectionTableRef.current.offsetHeight;
      setMeasuredLastSectionBottom(prev => (prev === bottom ? prev : bottom));
    }
  });

  // Helper to sync local and parent data
  const updateLocalData = (newData: RepairEstimateData) => {
    setLocalData(newData);
    if (updateRepairEstimateData) {
      updateRepairEstimateData(newData);
    }
  };

  // Single return for both PDF and preview
  return (
    <div className={styles.page} ref={pageContainerRef}>
      <div className={styles.overlapWrapper}>
        <div className={styles.overlap}>
          {/* Header Section */}
          <div className={styles.title}>
            <div className={styles.overlapGroup}>
              <div
                className={styles.textWrapper2}
                style={{
                  top: isPDF ? '4px' : '8px'
                }}
              >
                Repair Estimate
              </div>
            </div>
            <img className={styles.logo} alt="Logo" src="/logo.webp" />
          </div>




          {/* Render all sections on current page */}
          {sectionsOnCurrentPage.length > 0 && currentPageSectionPositions.map((sectionData, localIndex) => {
            const section = sectionData.section;
            const pos = sectionData.position;
            const sectionHeight = sectionData.height;
            const sectionTotalWidth = sectionData.totalWidth;
            const sectionPriceWidth = sectionData.priceWidth;
            const showTitle = allSections.length > 1;

            return (
              <React.Fragment key={section.title || `section-${localIndex}`}>
                {/* Section Title - Only show if there are multiple sections - Moves with table when dragged */}
                {showTitle && (
                  <div
                    style={{
                      position: 'absolute',
                      top: `${pos.top}px`,
                      left: '29px',
                      right: '29px',
                      fontSize: '14px',
                      fontWeight: 600,
                      pointerEvents: 'none' // Don't interfere with table dragging
                    }}
                  >
                    {section.title}
                  </div>
                )}

                {/* Section Table - Draggable in preview mode */}
                <div
                  ref={localIndex === currentPageSectionPositions.length - 1 ? lastSectionTableRef : null}
                  style={{
                    position: 'absolute',
                    top: `${pos.tableTop}px`,
                    left: '29px',
                    right: '29px',
                    fontSize: '12px',
                    fontFamily: 'Inter, Arial, sans-serif',
                    cursor: !isPDF ? (draggingTableId === sectionData.tableId ? 'grabbing' : 'grab') : 'default'
                  }}
                  onMouseDown={!isPDF ? (e) => handleTableMouseDown(e, sectionData.tableId, pos.tableTop) : undefined}
                >
                  <div
                    ref={localIndex === 0 ? tableRef : null}
                    style={{
                      display: 'grid',
                      gridTemplateColumns: `3fr 50px ${sectionPriceWidth}px ${sectionTotalWidth}px`,
                      gridAutoRows: 'minmax(15px, auto)',
                      gap: '0px',
                      backgroundColor: '#722420 ',
                    }}
                  >
                    {/* Header Row */}
                    <div style={{ display: 'contents' }}>
                      <div style={{
                        backgroundColor: '#722420',
                        color: 'white',
                        fontWeight: 'bold',
                        minHeight: isPDF ? '20px' : '20px',
                        padding: '0px 8px',
                        ...(isPDF ? {
                          display: 'table-cell',
                          verticalAlign: 'middle',
                          textAlign: 'center',
                          height: '20px',
                          boxSizing: 'border-box',
                          lineHeight: '1.0'
                        } : {
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          textAlign: 'center'
                        }),
                        borderBottom: '1px solid #722420',
                        fontSize: '12px',
                        wordWrap: 'break-word',
                        overflowWrap: 'break-word',
                        whiteSpace: 'normal'
                      }}>Description</div>
                      <div style={{
                        backgroundColor: '#722420',
                        color: 'white',
                        fontWeight: 'bold',
                        minHeight: isPDF ? '20px' : '20px',
                        padding: '0px 8px',
                        ...(isPDF ? {
                          display: 'table-cell',
                          verticalAlign: 'middle',
                          textAlign: 'center',
                          height: '20px',
                          boxSizing: 'border-box',
                          lineHeight: '1.0'
                        } : {
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          textAlign: 'center'
                        }),
                        borderBottom: '1px solid #722420',
                        fontSize: '12px',
                        wordWrap: 'break-word',
                        overflowWrap: 'break-word',
                        whiteSpace: 'normal'
                      }}>Unit</div>
                      <div style={{
                        backgroundColor: '#722420',
                        color: 'white',
                        fontWeight: 'bold',
                        minHeight: isPDF ? '20px' : '20px',
                        padding: '0px 8px',
                        ...(isPDF ? {
                          display: 'table-cell',
                          verticalAlign: 'middle',
                          textAlign: 'center',
                          height: '20px',
                          boxSizing: 'border-box',
                          lineHeight: '1.0'
                        } : {
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          textAlign: 'center'
                        }),
                        borderBottom: '1px solid #722420',
                        fontSize: '12px',
                        wordWrap: 'break-word',
                        overflowWrap: 'break-word',
                        whiteSpace: 'normal'
                      }}>Price</div>
                      <div style={{
                        backgroundColor: '#722420',
                        color: 'white',
                        fontWeight: 'bold',
                        minHeight: isPDF ? '20px' : '20px',
                        padding: '0px 8px',
                        ...(isPDF ? {
                          display: 'table-cell',
                          verticalAlign: 'middle',
                          textAlign: 'center',
                          height: '20px',
                          boxSizing: 'border-box',
                          lineHeight: '1.0'
                        } : {
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          textAlign: 'center'
                        }),
                        borderBottom: '1px solid #722420',
                        fontSize: '12px',
                        wordWrap: 'break-word',
                        overflowWrap: 'break-word',
                        whiteSpace: 'normal'
                      }}>Total</div>
                    </div>
                    {/* Data Rows */}
                    {section.rows.map((row: RepairEstimateRow, rowIndex: number) => {
                      const unitPrice = parseFloat(row.unit) || 0;
                      const price = parseFloat(row.price) || 0;
                      const total = unitPrice * price;

                      // Calculate how many rows are needed based on content length
                      const calculateRowsNeeded = (text: string, baseLength: number = 50) => {
                        if (!text || text.trim().length === 0) return 1;

                        const lines = text.split('\n').length;
                        const wordCount = text.split(' ').length;
                        const charCount = text.length;

                        // Priority 1: Explicit line breaks (from Enter key) take highest priority
                        // Each line break creates a new visual line
                        let rowsNeeded = lines;

                        // Priority 2: For URLs or long strings without spaces, use character count
                        // URLs typically wrap at around 40-45 chars in a narrow column
                        const isURL = text.includes('http://') || text.includes('https://') || text.includes('www.');
                        const effectiveBaseLength = isURL ? 40 : baseLength;

                        // For each line, calculate if it needs to wrap
                        const textLines = text.split('\n');
                        let totalCharRows = 0;
                        textLines.forEach(line => {
                          const lineCharRows = Math.ceil(line.length / effectiveBaseLength);
                          totalCharRows += Math.max(1, lineCharRows);
                        });

                        // Use the maximum of explicit lines or character-based wrapping
                        rowsNeeded = Math.max(rowsNeeded, totalCharRows);

                        // Priority 3: For text with spaces, also consider word count (but only if no explicit line breaks)
                        if (lines === 1 && wordCount > 1) {
                          const wordRows = Math.ceil(wordCount / 8);
                          rowsNeeded = Math.max(rowsNeeded, wordRows);
                        }

                        // Ensure minimum of 1 row
                        return Math.max(rowsNeeded, 1);
                      };

                      const descriptionRows = calculateRowsNeeded(row.description);
                      const unitRows = calculateRowsNeeded(row.unit, 10);
                      const priceRows = calculateRowsNeeded(row.price, 10);

                      const maxRowsNeeded = Math.max(descriptionRows, unitRows, priceRows);
                      const isLongContent = maxRowsNeeded > 1;
                      const rowSpan = maxRowsNeeded;
                      const descriptionHeight = estimateRowBoxHeight(rowSpan);

                      return (
                        <div key={row.id} style={{ display: 'contents' }}>
                        <div style={{
                      backgroundColor: rowIndex % 2 === 0 ? '#ffffff' : '#f8f9fa',
                      color: '#000000',
                      fontWeight: 'normal',
                      // minHeight: `${descriptionHeight}px`,
                      padding: isPDF ? '0px 8px' : '4px 0px',
                      ...(isPDF ? {
                        display: 'table-cell',
                        verticalAlign: 'top',
                        textAlign: 'left',
                        lineHeight: '1.4', // Increased from 1.0 to 1.4 for better spacing
                        minHeight: `${descriptionHeight}px`, // Use minHeight instead of fixed height
                        boxSizing: 'border-box',
                        paddingTop: '4px',
                        paddingBottom: '4px'
                      } : {
                        display: 'flex',
                        alignItems: 'flex-start',
                        justifyContent: 'left',
                        textAlign: 'left',
                        lineHeight: '1.4'
                      }),
                      fontSize: '12px',
                      wordWrap: 'break-word',
                      overflowWrap: 'break-word',
                      whiteSpace: 'pre-wrap',
                      maxWidth: '100%',
                      overflow: 'hidden',
                      hyphens: 'auto',
                      wordBreak: 'normal'
                    }}>
                      {row.description}
                    </div>
                        <div style={{
                          backgroundColor: rowIndex % 2 === 0 ? '#ffffff' : '#f8f9fa',
                          color: '#000000',
                          fontWeight: 'normal',
                          // minHeight: `${descriptionHeight}px`,
                          padding: isPDF ? '0px 8px' : '4px 8px',
                          ...(isPDF ? {
                            display: 'table-cell',
                            verticalAlign: 'middle',
                            textAlign: 'center',
                            minHeight: `${descriptionHeight}px`,
                            lineHeight: '1.4',
                            boxSizing: 'border-box',
                            paddingTop: '4px',
                            paddingBottom: '4px'
                          } : {
                            display:  'flex',
                            alignItems: 'flex-start',
                            justifyContent: 'center',
                            textAlign: 'center'
                          }),
                          fontSize: '12px',
                          wordWrap: 'break-word',
                          overflowWrap: 'break-word',
                          whiteSpace: 'pre-wrap',
                          maxWidth: '100%',
                          overflow: 'hidden',
                          hyphens: 'auto',
                          wordBreak: 'normal'
                        }}>
                          {row.unit}
                        </div>
                        <div style={{
                          backgroundColor: rowIndex % 2 === 0 ? '#ffffff' : '#f8f9fa',
                          color: '#000000',
                          fontWeight: 'normal',
                          // minHeight: `${descriptionHeight}px`,
                          padding: isPDF ? '8px 8px' : '4px 8px',
                          ...(isPDF ? {
                            display: 'table-cell',
                            verticalAlign: 'middle',
                            textAlign: 'center',
                            lineHeight: '1.4',
                            minHeight: `${descriptionHeight}px`,
                            boxSizing: 'border-box',
                            paddingTop: '4px',
                            paddingBottom: '4px'
                          } : {
                            display: 'flex',
                            alignItems: 'flex-start',
                            justifyContent: 'center',
                            textAlign: 'center'
                          }),
                          fontSize: '12px',
                          wordWrap: 'break-word',
                          overflowWrap: 'break-word',
                          whiteSpace: 'pre-wrap',
                          maxWidth: '100%',
                          overflow: 'hidden',
                          hyphens: 'auto',
                          wordBreak: 'normal'
                        }}>
                          {row.price}
                        </div>
                        <div style={{
                          backgroundColor: rowIndex % 2 === 0 ? '#ffffff' : '#f8f9fa',
                          color: '#000000',
                          fontWeight: '600',
                          //  minHeight: `${descriptionHeight}px`,
                          padding: isPDF ? '8px 8px' : '4px 8px',
                          ...(isPDF ? {
                            display: 'table-cell',
                            verticalAlign: 'middle',
                            textAlign: 'center',
                            lineHeight: '1.4',
                            minHeight: `${descriptionHeight}px`,
                            boxSizing: 'border-box',
                            paddingTop: '4px',
                            paddingBottom: '4px'
                          } : {
                            display: 'flex',
                            alignItems: 'flex-start',
                            justifyContent: 'center',
                            textAlign: 'center'
                          }),
                          fontSize: '12px',
                          wordWrap: 'break-word',
                          overflowWrap: 'break-word',
                          whiteSpace: 'pre-wrap',
                          maxWidth: '100%',
                          overflow: 'hidden',
                          hyphens: 'auto',
                          wordBreak: 'normal'
                        }}>
                          {total.toFixed(2)}
                        </div>
                        {/* Add empty rows for merged cells */}
                        {isLongContent && Array.from({ length: rowSpan - 1 }, (_, i) => (
                          <div key={`empty-${i}`} style={{ display: 'contents' }}>
                            <div style={{ display: 'none' }}></div>
                            <div style={{ display: 'none' }}></div>
                            <div style={{ display: 'none' }}></div>
                            <div style={{ display: 'none' }}></div>
                          </div>
                        ))}
                      </div>
                      );
                    })}

                    {/* Section Total */}
                    {section.rows.length > 0 && (
                      <div style={{ display: 'contents' }}>
                        <div style={{
                          backgroundColor: '#722420',
                          color: 'white',
                          fontWeight: 'bold',
                          minHeight: isPDF ? '20px' : '20px',
                          padding: '4px 8px',
                          ...(isPDF ? {
                            display: 'table-cell',
                            verticalAlign: 'middle',
                            textAlign: 'center',
                            height: `${20}px`,
                            boxSizing: 'border-box',
                            paddingTop: '2px',
                          } : {
                            display: 'flex',
                            alignItems: 'flex-start',
                            justifyContent: 'center',
                            textAlign: 'center'
                          }),
                          fontSize: '12px',
                          wordWrap: 'break-word',
                          overflowWrap: 'break-word',
                          whiteSpace: 'normal'
                        }}></div>
                        <div style={{
                          backgroundColor: '#722420',
                          color: 'white',
                          fontWeight: 'bold',
                          minHeight: isPDF ? '20px' : '20px',
                          padding: '4px 8px',
                          ...(isPDF ? {
                            display: 'table-cell',
                            verticalAlign: 'middle',
                            textAlign: 'center',
                            height: `${20}px`,
                            boxSizing: 'border-box',
                            paddingTop: '2px',
                          } : {}),
                        }}></div>
                        <div style={{
                          backgroundColor: '#722420',
                          color: 'white',
                          fontWeight: 'bold',
                          minHeight: isPDF ? '20px' : '20px',
                          padding: '0px 8px',
                          ...(isPDF ? {
                            display: 'table-cell',
                            verticalAlign: 'middle',
                            textAlign: 'center',
                            height: '20px',
                            boxSizing: 'border-box',
                            lineHeight: '1.0'
                          } : {
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            textAlign: 'center'
                          }),
                          fontSize: '12px',
                          wordWrap: 'break-word',
                          overflowWrap: 'break-word',
                          whiteSpace: 'normal'
                        }}>
                          TOTAL:
                        </div>
                        <div style={{
                          backgroundColor: '#722420',
                          color: 'white',
                          fontWeight: 'bold',
                          minHeight: isPDF ? '20px' : '20px',
                          padding: '0px 8px',
                          ...(isPDF ? {
                            display: 'table-cell',
                            verticalAlign: 'middle',
                            textAlign: 'center',
                            height: '20px',
                            boxSizing: 'border-box',
                            lineHeight: '1.0'
                          } : {
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            textAlign: 'center'
                          }),
                          fontSize: '12px',
                          whiteSpace: 'nowrap'
                        }}>
                          ${(section.rows || []).reduce((sum: number, row: RepairEstimateRow) => {
                            const unitPrice = parseFloat(row.unit) || 0;
                            const price = parseFloat(row.price) || 0;
                            return sum + (unitPrice * price);
                          }, 0).toFixed(2)}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </React.Fragment>
            );
          })}

          {/* Legacy Section 1 - Keep for backward compatibility, only show if no new sections */}
          {(!sections || sections.length === 0) && !isSummaryOnlyPage && effectiveSection1.rows.length > 0 && (
            <>
              {/* Section 1 Title - Only show if there are 2 sections */}
              {effectiveSection2 && (
                <div style={{ position: 'absolute', top: `${section1Top}px`, left: '29px', right: '29px', fontSize: '14px', fontWeight: 600 }}>
                  {effectiveSection1.title}
                </div>
              )}

              {/* Section 1 Table */}
              <div style={{
                position: 'absolute',
                top: `${section1TableTop}px`,
                left: '29px',
                right: '29px',
                fontSize: '12px',
                fontFamily: 'Inter, Arial, sans-serif'
              }}>
                <div
                  ref={tableRef}
                  style={{
                    display: 'grid',
                    gridTemplateColumns: `3fr 50px ${section1PriceWidth}px ${section1TotalWidth}px`,
                    gridAutoRows: 'minmax(15px, auto)',
                    gap: '0px',
                    backgroundColor: '#722420 ',
                  }}
                >
                  {/* Header Row */}
                  <div style={{ display: 'contents' }}>
                    <div style={{
                      backgroundColor: '#722420',
                      color: 'white',
                      fontWeight: 'bold',
                      minHeight: isPDF ? '20px' : '20px',
                      padding: '0px 8px',
                      ...(isPDF ? {
                        display: 'table-cell',
                        verticalAlign: 'middle',
                        textAlign: 'center',
                        height: '20px',
                        boxSizing: 'border-box',
                        lineHeight: '1.0'
                      } : {
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        textAlign: 'center'
                      }),
                      borderBottom: '1px solid #722420',
                      fontSize: '12px',
                      wordWrap: 'break-word',
                      overflowWrap: 'break-word',
                      whiteSpace: 'normal'
                    }}>Description</div>
                    <div style={{
                      backgroundColor: '#722420',
                      color: 'white',
                      fontWeight: 'bold',
                      minHeight: isPDF ? '20px' : '20px',
                      padding: '0px 8px',
                      ...(isPDF ? {
                        display: 'table-cell',
                        verticalAlign: 'middle',
                        textAlign: 'center',
                        height: '20px',
                        boxSizing: 'border-box',
                        lineHeight: '1.0'
                      } : {
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        textAlign: 'center'
                      }),
                      borderBottom: '1px solid #722420',
                      fontSize: '12px',
                      wordWrap: 'break-word',
                      overflowWrap: 'break-word',
                      whiteSpace: 'normal'
                    }}>Unit</div>
                    <div style={{
                      backgroundColor: '#722420',
                      color: 'white',
                      fontWeight: 'bold',
                      minHeight: isPDF ? '20px' : '20px',
                      padding: '0px 8px',
                      ...(isPDF ? {
                        display: 'table-cell',
                        verticalAlign: 'middle',
                        textAlign: 'center',
                        height: '20px',
                        boxSizing: 'border-box',
                        lineHeight: '1.0'
                      } : {
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        textAlign: 'center'
                      }),
                      borderBottom: '1px solid #722420',
                      fontSize: '12px',
                      wordWrap: 'break-word',
                      overflowWrap: 'break-word',
                      whiteSpace: 'normal'
                    }}>Price</div>
                    <div style={{
                      backgroundColor: '#722420',
                      color: 'white',
                      fontWeight: 'bold',
                      minHeight: isPDF ? '20px' : '20px',
                      padding: '0px 8px',
                      ...(isPDF ? {
                        display: 'table-cell',
                        verticalAlign: 'middle',
                        textAlign: 'center',
                        height: '20px',
                        boxSizing: 'border-box',
                        lineHeight: '1.0'
                      } : {
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        textAlign: 'center'
                      }),
                      borderBottom: '1px solid #722420',
                      fontSize: '12px',
                      wordWrap: 'break-word',
                      overflowWrap: 'break-word',
                      whiteSpace: 'normal'
                    }}>Total</div>
                  </div>
                  {/* Data Rows */}
                  {effectiveSection1.rows.map((row, index) => {
                    const unitPrice = parseFloat(row.unit) || 0;
                    const price = parseFloat(row.price) || 0;
                    const total = unitPrice * price;

                    // Calculate how many rows are needed based on content length
                    const calculateRowsNeeded = (text: string, baseLength: number = 50) => {
                      if (!text || text.trim().length === 0) return 1;

                      const lines = text.split('\n').length;
                      const wordCount = text.split(' ').length;
                      const charCount = text.length;

                      // Priority 1: Explicit line breaks (from Enter key) take highest priority
                      // Each line break creates a new visual line
                      let rowsNeeded = lines;

                      // Priority 2: For URLs or long strings without spaces, use character count
                      // URLs typically wrap at around 40-45 chars in a narrow column
                      const isURL = text.includes('http://') || text.includes('https://') || text.includes('www.');
                      const effectiveBaseLength = isURL ? 40 : baseLength;

                      // For each line, calculate if it needs to wrap
                      const textLines = text.split('\n');
                      let totalCharRows = 0;
                      textLines.forEach(line => {
                        const lineCharRows = Math.ceil(line.length / effectiveBaseLength);
                        totalCharRows += Math.max(1, lineCharRows);
                      });

                      // Use the maximum of explicit lines or character-based wrapping
                      rowsNeeded = Math.max(rowsNeeded, totalCharRows);

                      // Priority 3: For text with spaces, also consider word count (but only if no explicit line breaks)
                      if (lines === 1 && wordCount > 1) {
                        const wordRows = Math.ceil(wordCount / 8);
                        rowsNeeded = Math.max(rowsNeeded, wordRows);
                      }

                      // Ensure minimum of 1 row
                      return Math.max(rowsNeeded, 1);
                    };

                    const descriptionRows = calculateRowsNeeded(row.description);
                    const unitRows = calculateRowsNeeded(row.unit, 10);
                    const priceRows = calculateRowsNeeded(row.price, 10);

                    const maxRowsNeeded = Math.max(descriptionRows, unitRows, priceRows);
                    const isLongContent = maxRowsNeeded > 1;
                    const rowSpan = maxRowsNeeded;

                    // Calculate proper height for PDF rendering to prevent overlaps
                    const baseRowHeight = isPDF ? 14 : 14;
                    const calculatedHeight = baseRowHeight * rowSpan;

                    // Special height calculation for description column (taller for better text display)
                    const descriptionHeight = isPDF ? (15 * rowSpan) : (15 * rowSpan);

                    return (
                      0
                    );
                  })}

                  {/* Section 1 Total */}

                </div>
              </div>
            </>
          )}


          {/* Summary Table - Only show when more than one section exists, at the end after all tables */}
          {showSummaryOnCurrentPage && allSections.length > 1 && (
            <>
              {/* Summary Title - Draggable in preview mode */}
              <div
                style={{
                  position: 'absolute',
                  top: `${summaryTop}px`,
                  left: '29px',
                  right: '29px',
                  fontSize: '14px',
                  fontWeight: 600,
                  cursor: !isPDF ? (draggingTableId === summaryTableId ? 'grabbing' : 'grab') : 'default'
                }}
                onMouseDown={!isPDF ? (e) => handleTableMouseDown(e, summaryTableId, summaryTop) : undefined}
              >
                Summary
              </div>

              {/* Summary Table */}
              <div style={{
                position: 'absolute',
                top: `${summaryTableTop}px`,
                left: '29px',
                right: '29px',
                fontSize: '12px',
                fontFamily: 'Inter, Arial, sans-serif'
              }}>
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: `3fr 50px ${summaryPriceWidth}px ${summaryTotalWidth}px`,
                  gridAutoRows: 'minmax(15px, auto)',
                  gap: '0px',
                  backgroundColor: '#722420 ',
                }}>
                  {/* Header Row */}
                  <div style={{ display: 'contents' }}>
                    <div style={{
                      backgroundColor: '#722420',
                      color: 'white',
                      fontWeight: 'bold',
                      minHeight: isPDF ? '20px' : '20px',
                      padding: '0px 8px',
                      ...(isPDF ? {
                        display: 'table-cell',
                        verticalAlign: 'middle',
                        textAlign: 'center',
                        height: '20px',
                        boxSizing: 'border-box',
                        lineHeight: '1.0'
                      } : {
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        textAlign: 'center'
                      }),
                      fontSize: '12px',
                      wordWrap: 'break-word',
                      overflowWrap: 'break-word',
                      whiteSpace: 'normal'
                    }}>Section</div>
                    <div style={{
                      backgroundColor: '#722420',
                      color: 'white',
                      fontWeight: 'bold',
                      minHeight: isPDF ? '20px' : '20px',
                      padding: '0px 8px',
                      ...(isPDF ? {
                        display: 'table-cell',
                        verticalAlign: 'middle',
                        textAlign: 'center',
                        height: '20px',
                        boxSizing: 'border-box',
                        lineHeight: '1.0'
                      } : {
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        textAlign: 'center'
                      }),
                      fontSize: '12px',
                      wordWrap: 'break-word',
                      overflowWrap: 'break-word',
                      whiteSpace: 'normal'
                    }}></div>
                    <div style={{
                      backgroundColor: '#722420',
                      color: 'white',
                      fontWeight: 'bold',
                      minHeight: isPDF ? '20px' : '20px',
                      padding: '0px 8px',
                      ...(isPDF ? {
                        display: 'table-cell',
                        verticalAlign: 'middle',
                        textAlign: 'center',
                        height: '20px',
                        boxSizing: 'border-box',
                        lineHeight: '1.0'
                      } : {
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        textAlign: 'center'
                      }),
                      fontSize: '12px',
                      wordWrap: 'break-word',
                      overflowWrap: 'break-word',
                      whiteSpace: 'normal'
                    }}></div>
                    <div style={{
                      backgroundColor: '#722420',
                      color: 'white',
                      fontWeight: 'bold',
                      minHeight: isPDF ? '20px' : '20px',
                      padding: '0px 8px',
                      ...(isPDF ? {
                        display: 'table-cell',
                        verticalAlign: 'middle',
                        textAlign: 'center',
                        height: '20px',
                        boxSizing: 'border-box',
                        lineHeight: '1.0'
                      } : {
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        textAlign: 'center'
                      }),
                      fontSize: '12px',
                      wordWrap: 'break-word',
                      overflowWrap: 'break-word',
                      whiteSpace: 'normal'
                    }}>Total</div>
                  </div>

                  {/* Render all sections dynamically */}
                  {allSections.map((section, sectionIndex) => {
                    const sectionTotal = (section.rows || []).reduce((sum, row) => {
                      const unitPrice = parseFloat(row.unit) || 0;
                      const price = parseFloat(row.price) || 0;
                      return sum + (unitPrice * price);
                    }, 0);
                    const isEven = sectionIndex % 2 === 0;

                    return (
                      <div key={sectionIndex} style={{ display: 'contents' }}>
                        <div style={{
                          backgroundColor: isEven ? '#ffffff' : '#f8f9fa',
                          color: '#000000',
                          fontWeight: 'normal',
                          minHeight: isPDF ? '20px' : '20px',
                          padding: '0px 8px',
                          ...(isPDF ? {
                            display: 'table-cell',
                            verticalAlign: 'middle',
                            textAlign: 'left',
                            height: '20px',
                            boxSizing: 'border-box',
                            lineHeight: '1.0'
                          } : {
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'flex-start',
                            textAlign: 'left'
                          }),
                          fontSize: '12px',
                          wordWrap: 'break-word',
                          overflowWrap: 'break-word',
                          whiteSpace: 'normal'
                        }}>
                          {section.title}
                        </div>
                        <div style={{
                          backgroundColor: isEven ? '#ffffff' : '#f8f9fa',
                          color: '#000000',
                          fontWeight: 'normal',
                          minHeight: isPDF ? '20px' : '20px',
                          padding: '0px 8px',
                          ...(isPDF ? {
                            display: 'table-cell',
                            verticalAlign: 'middle',
                            textAlign: 'center',
                            height: '20px',
                            boxSizing: 'border-box',
                            lineHeight: '1.0'
                          } : {
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            textAlign: 'center'
                          }),
                          fontSize: '12px',
                          wordWrap: 'break-word',
                          overflowWrap: 'break-word',
                          whiteSpace: 'normal'
                        }}></div>
                        <div style={{
                          backgroundColor: isEven ? '#ffffff' : '#f8f9fa',
                          color: '#000000',
                          fontWeight: 'normal',
                          minHeight: isPDF ? '20px' : '20px',
                          padding: '0px 8px',
                          ...(isPDF ? {
                            display: 'table-cell',
                            verticalAlign: 'middle',
                            textAlign: 'center',
                            height: '20px',
                            boxSizing: 'border-box',
                            lineHeight: '1.0'
                          } : {
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            textAlign: 'center'
                          }),
                          fontSize: '12px',
                          wordWrap: 'break-word',
                          overflowWrap: 'break-word',
                          whiteSpace: 'normal'
                        }}></div>
                        <div style={{
                          backgroundColor: isEven ? '#ffffff' : '#f8f9fa',
                          color: '#000000',
                          fontWeight: '600',
                          minHeight: isPDF ? '20px' : '20px',
                          padding: '0px 8px',
                          ...(isPDF ? {
                            display: 'table-cell',
                            verticalAlign: 'middle',
                            textAlign: 'center',
                            height: '20px',
                            boxSizing: 'border-box',
                            lineHeight: '1.0'
                          } : {
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            textAlign: 'center'
                          }),
                          fontSize: '12px',
                          whiteSpace: 'nowrap'
                        }}>
                          ${sectionTotal.toFixed(2)}
                        </div>
                      </div>
                    );
                  })}

                  {/* Grand Total Row */}
                  <div style={{ display: 'contents' }}>
                    <div style={{
                      backgroundColor: '#722420',
                      color: 'white',
                      fontWeight: 'bold',
                      minHeight: isPDF ? '20px' : '20px',
                      padding: '0px 8px',
                      ...(isPDF ? {
                        display: 'table-cell',
                        verticalAlign: 'middle',
                        textAlign: 'left',
                        height: '20px',
                        boxSizing: 'border-box',
                        lineHeight: '1.0'
                      } : {
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'flex-start',
                        textAlign: 'left'
                      }),
                      fontSize: '12px',
                      wordWrap: 'break-word',
                      overflowWrap: 'break-word',
                      whiteSpace: 'normal'
                    }}>
                      GRAND TOTAL
                    </div>
                    <div style={{
                      backgroundColor: '#722420',
                      color: 'white',
                      fontWeight: 'bold',
                      minHeight: isPDF ? '20px' : '20px',
                      padding: '0px 8px',
                      ...(isPDF ? {
                        display: 'table-cell',
                        verticalAlign: 'middle',
                        textAlign: 'center',
                        height: '20px',
                        boxSizing: 'border-box',
                        lineHeight: '1.0'
                      } : {
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        textAlign: 'center'
                      }),
                      fontSize: '12px',
                      wordWrap: 'break-word',
                      overflowWrap: 'break-word',
                      whiteSpace: 'normal'
                    }}></div>
                    <div style={{
                      backgroundColor: '#722420',
                      color: 'white',
                      fontWeight: 'bold',
                      minHeight: isPDF ? '20px' : '20px',
                      padding: '0px 8px',
                      ...(isPDF ? {
                        display: 'table-cell',
                        verticalAlign: 'middle',
                        textAlign: 'center',
                        height: '20px',
                        boxSizing: 'border-box',
                        lineHeight: '1.0'
                      } : {
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        textAlign: 'center'
                      }),
                      fontSize: '12px',
                      wordWrap: 'break-word',
                      overflowWrap: 'break-word',
                      whiteSpace: 'normal'
                    }}></div>
                    <div style={{
                      backgroundColor: '#722420',
                      color: 'white',
                      fontWeight: 'bold',
                      minHeight: isPDF ? '20px' : '20px',
                      padding: '0px 8px',
                      ...(isPDF ? {
                        display: 'table-cell',
                        verticalAlign: 'middle',
                        textAlign: 'center',
                        height: '20px',
                        boxSizing: 'border-box',
                        lineHeight: '1.0'
                      } : {
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        textAlign: 'center'
                      }),
                      fontSize: '12px',
                      whiteSpace: 'nowrap'
                    }}>
                      ${allSections.reduce((sum, section) => {
                        return sum + (section.rows || []).reduce((sectionSum, row) => {
                          const unitPrice = parseFloat(row.unit) || 0;
                          const price = parseFloat(row.price) || 0;
                          return sectionSum + (unitPrice * price);
                        }, 0);
                      }, 0).toFixed(2)}
                    </div>
                  </div>
                </div>
              </div>

              {/* Notes Section - Grouped with Summary - Draggable in preview mode */}
              {notes && showNotesOnCurrentPage && (
                <>
                  <div
                    style={{
                      position: 'absolute',
                      top: `${notesTop}px`,
                      left: '29px',
                      right: '29px',
                      fontSize: '12px',
                      fontFamily: 'Inter, Arial, sans-serif',
                      color: '#000000',
                      lineHeight: '1.4',
                      cursor: !isPDF ? (draggingTableId === notesTableId ? 'grabbing' : 'grab') : 'default'
                    }}
                    onMouseDown={!isPDF ? (e) => handleTableMouseDown(e, notesTableId, notesTop) : undefined}
                  >
                    <div style={{ fontWeight: 'bold', marginBottom: '8px', color: '#722420' }}>
                      Notes:
                    </div>
                    <div style={{ paddingLeft: '10px', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                      {(() => {
                        const baseNotes = notes || '';
                        if (dataSourceUrl && !baseNotes.includes('Link to view timeline:')) {
                          return `${baseNotes}\n\nLink to view timeline: ${dataSourceUrl}`;
                        }
                        return baseNotes;
                      })()}
                    </div>
                  </div>
                </>
              )}
            </>
          )}

          {/* Summary Table on Separate Page - When summary+notes don't fit on main page */}
          {isSummaryOnlyPage && showSummaryOnCurrentPage && (
            <>
              {/* Summary Title - Draggable in preview mode */}
              <div
                style={{
                  position: 'absolute',
                  top: `${!isPDF && customPositions[summaryTableId] !== undefined ? customPositions[summaryTableId] : 150}px`,
                  left: '29px',
                  right: '29px',
                  fontSize: '14px',
                  fontWeight: 600,
                  cursor: !isPDF ? (draggingTableId === summaryTableId ? 'grabbing' : 'grab') : 'default'
                }}
                onMouseDown={!isPDF ? (e) => handleTableMouseDown(e, summaryTableId, customPositions[summaryTableId] || 150) : undefined}
              >
                Summary
              </div>

              {/* Summary Table */}
              <div style={{
                position: 'absolute',
                top: `${(!isPDF && customPositions[summaryTableId] !== undefined ? customPositions[summaryTableId] : 150) + 30}px`,
                left: '29px',
                right: '29px',
                fontSize: '12px',
                fontFamily: 'Inter, Arial, sans-serif'
              }}>
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: `3fr 50px ${summaryPriceWidth}px ${summaryTotalWidth}px`,
                  gridAutoRows: 'minmax(15px, auto)',
                  gap: '0px',
                  backgroundColor: '#722420 ',
                }}>
                  {/* Header Row */}
                  <div style={{ display: 'contents' }}>
                    <div style={{
                      backgroundColor: '#722420',
                      color: 'white',
                      fontWeight: 'bold',
                      minHeight: isPDF ? '20px' : '20px',
                      padding: '0px 8px',
                      ...(isPDF ? {
                        display: 'table-cell',
                        verticalAlign: 'middle',
                        textAlign: 'center',
                        height: '20px',
                        boxSizing: 'border-box',
                        lineHeight: '1.0'
                      } : {
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        textAlign: 'center'
                      }),
                      fontSize: '12px',
                      wordWrap: 'break-word',
                      overflowWrap: 'break-word',
                      whiteSpace: 'normal'
                    }}>Section</div>
                    <div style={{
                      backgroundColor: '#722420',
                      color: 'white',
                      fontWeight: 'bold',
                      minHeight: isPDF ? '20px' : '20px',
                      padding: '0px 8px',
                      ...(isPDF ? {
                        display: 'table-cell',
                        verticalAlign: 'middle',
                        textAlign: 'center',
                        height: '20px',
                        boxSizing: 'border-box',
                        lineHeight: '1.0'
                      } : {
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        textAlign: 'center'
                      }),
                      fontSize: '12px',
                      wordWrap: 'break-word',
                      overflowWrap: 'break-word',
                      whiteSpace: 'normal'
                    }}></div>
                    <div style={{
                      backgroundColor: '#722420',
                      color: 'white',
                      fontWeight: 'bold',
                      minHeight: isPDF ? '20px' : '20px',
                      padding: '0px 8px',
                      ...(isPDF ? {
                        display: 'table-cell',
                        verticalAlign: 'middle',
                        textAlign: 'center',
                        height: '20px',
                        boxSizing: 'border-box',
                        lineHeight: '1.0'
                      } : {
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        textAlign: 'center'
                      }),
                      fontSize: '12px',
                      wordWrap: 'break-word',
                      overflowWrap: 'break-word',
                      whiteSpace: 'normal'
                    }}></div>
                    <div style={{
                      backgroundColor: '#722420',
                      color: 'white',
                      fontWeight: 'bold',
                      minHeight: isPDF ? '20px' : '20px',
                      padding: '0px 8px',
                      ...(isPDF ? {
                        display: 'table-cell',
                        verticalAlign: 'middle',
                        textAlign: 'center',
                        height: '20px',
                        boxSizing: 'border-box',
                        lineHeight: '1.0'
                      } : {
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        textAlign: 'center'
                      }),
                      fontSize: '12px',
                      wordWrap: 'break-word',
                      overflowWrap: 'break-word',
                      whiteSpace: 'normal'
                    }}>Total</div>
                  </div>

                  {/* Render all sections dynamically */}
                  {allSections.map((section, sectionIndex) => {
                    const sectionTotal = (section.rows || []).reduce((sum, row) => {
                      const unitPrice = parseFloat(row.unit) || 0;
                      const price = parseFloat(row.price) || 0;
                      return sum + (unitPrice * price);
                    }, 0);
                    const isEven = sectionIndex % 2 === 0;

                    return (
                      <div key={sectionIndex} style={{ display: 'contents' }}>
                        <div style={{
                          backgroundColor: isEven ? '#ffffff' : '#f8f9fa',
                          color: '#000000',
                          fontWeight: 'normal',
                          minHeight: isPDF ? '20px' : '20px',
                          padding: '0px 8px',
                          ...(isPDF ? {
                            display: 'table-cell',
                            verticalAlign: 'middle',
                            textAlign: 'left',
                            height: '20px',
                            boxSizing: 'border-box',
                            lineHeight: '1.0'
                          } : {
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'flex-start',
                            textAlign: 'left'
                          }),
                          fontSize: '12px',
                          wordWrap: 'break-word',
                          overflowWrap: 'break-word',
                          whiteSpace: 'normal'
                        }}>
                          {section.title}
                        </div>
                        <div style={{
                          backgroundColor: isEven ? '#ffffff' : '#f8f9fa',
                          color: '#000000',
                          fontWeight: 'normal',
                          minHeight: isPDF ? '20px' : '20px',
                          padding: '0px 8px',
                          ...(isPDF ? {
                            display: 'table-cell',
                            verticalAlign: 'middle',
                            textAlign: 'center',
                            height: '20px',
                            boxSizing: 'border-box',
                            lineHeight: '1.0'
                          } : {
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            textAlign: 'center'
                          }),
                          fontSize: '12px',
                          wordWrap: 'break-word',
                          overflowWrap: 'break-word',
                          whiteSpace: 'normal'
                        }}></div>
                        <div style={{
                          backgroundColor: isEven ? '#ffffff' : '#f8f9fa',
                          color: '#000000',
                          fontWeight: 'normal',
                          minHeight: isPDF ? '20px' : '20px',
                          padding: '0px 8px',
                          ...(isPDF ? {
                            display: 'table-cell',
                            verticalAlign: 'middle',
                            textAlign: 'center',
                            height: '20px',
                            boxSizing: 'border-box',
                            lineHeight: '1.0'
                          } : {
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            textAlign: 'center'
                          }),
                          fontSize: '12px',
                          wordWrap: 'break-word',
                          overflowWrap: 'break-word',
                          whiteSpace: 'normal'
                        }}></div>
                        <div style={{
                          backgroundColor: isEven ? '#ffffff' : '#f8f9fa',
                          color: '#000000',
                          fontWeight: '600',
                          minHeight: isPDF ? '20px' : '20px',
                          padding: '0px 8px',
                          ...(isPDF ? {
                            display: 'table-cell',
                            verticalAlign: 'middle',
                            textAlign: 'center',
                            height: '20px',
                            boxSizing: 'border-box',
                            lineHeight: '1.0'
                          } : {
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            textAlign: 'center'
                          }),
                          fontSize: '12px',
                          whiteSpace: 'nowrap'
                        }}>
                          ${sectionTotal.toFixed(2)}
                        </div>
                      </div>
                    );
                  })}

                  {/* Grand Total Row */}
                  <div style={{ display: 'contents' }}>
                    <div style={{
                      backgroundColor: '#722420',
                      color: 'white',
                      fontWeight: 'bold',
                      minHeight: isPDF ? '20px' : '20px',
                      padding: '0px 8px',
                      ...(isPDF ? {
                        display: 'table-cell',
                        verticalAlign: 'middle',
                        textAlign: 'left',
                        height: '20px',
                        boxSizing: 'border-box',
                        lineHeight: '1.0'
                      } : {
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'flex-start',
                        textAlign: 'left'
                      }),
                      fontSize: '12px',
                      wordWrap: 'break-word',
                      overflowWrap: 'break-word',
                      whiteSpace: 'normal'
                    }}>
                      GRAND TOTAL
                    </div>
                    <div style={{
                      backgroundColor: '#722420',
                      color: 'white',
                      fontWeight: 'bold',
                      minHeight: isPDF ? '20px' : '20px',
                      padding: '0px 8px',
                      ...(isPDF ? {
                        display: 'table-cell',
                        verticalAlign: 'middle',
                        textAlign: 'center',
                        height: '20px',
                        boxSizing: 'border-box',
                        lineHeight: '1.0'
                      } : {
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        textAlign: 'center'
                      }),
                      fontSize: '12px',
                      wordWrap: 'break-word',
                      overflowWrap: 'break-word',
                      whiteSpace: 'normal'
                    }}></div>
                    <div style={{
                      backgroundColor: '#722420',
                      color: 'white',
                      fontWeight: 'bold',
                      minHeight: isPDF ? '20px' : '20px',
                      padding: '0px 8px',
                      ...(isPDF ? {
                        display: 'table-cell',
                        verticalAlign: 'middle',
                        textAlign: 'center',
                        height: '20px',
                        boxSizing: 'border-box',
                        lineHeight: '1.0'
                      } : {
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        textAlign: 'center'
                      }),
                      fontSize: '12px',
                      wordWrap: 'break-word',
                      overflowWrap: 'break-word',
                      whiteSpace: 'normal'
                    }}></div>
                    <div style={{
                      backgroundColor: '#722420',
                      color: 'white',
                      fontWeight: 'bold',
                      minHeight: isPDF ? '20px' : '20px',
                      padding: '0px 8px',
                      ...(isPDF ? {
                        display: 'table-cell',
                        verticalAlign: 'middle',
                        textAlign: 'center',
                        height: '20px',
                        boxSizing: 'border-box',
                        lineHeight: '1.0'
                      } : {
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        textAlign: 'center'
                      }),
                      fontSize: '12px',
                      whiteSpace: 'nowrap'
                    }}>
                      ${allSections.reduce((sum, section) => {
                        return sum + (section.rows || []).reduce((sectionSum, row) => {
                          const unitPrice = parseFloat(row.unit) || 0;
                          const price = parseFloat(row.price) || 0;
                          return sectionSum + (unitPrice * price);
                        }, 0);
                      }, 0).toFixed(2)}
                    </div>
                  </div>
                </div>
              </div>

              {/* Notes Section - Grouped with Summary on separate page - Draggable in preview mode */}
              {notes && showNotesOnCurrentPage && (
                <div
                  style={{
                    position: 'absolute',
                    top: `${!isPDF && customPositions[notesTableId] !== undefined
                      ? customPositions[notesTableId]
                      : 180 + actualSummaryTableHeight + (allSections.length > 9 ? 40 : 25)}px`, // Position below summary table (using actual height, more space for many sections)
                    left: '29px',
                    right: '29px',
                    fontSize: '12px',
                    fontFamily: 'Inter, Arial, sans-serif',
                    color: '#000000',
                    lineHeight: '1.4',
                    cursor: !isPDF ? (draggingTableId === notesTableId ? 'grabbing' : 'grab') : 'default'
                  }}
                  onMouseDown={!isPDF ? (e) => handleTableMouseDown(e, notesTableId, customPositions[notesTableId] || (180 + actualSummaryTableHeight + (allSections.length > 9 ? 40 : 25))) : undefined}
                >
                  <div style={{ fontWeight: 'bold', marginBottom: '8px', color: '#722420' }}>
                    Notes:
                  </div>
                  <div style={{ paddingLeft: '10px', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                    {(() => {
                      const baseNotes = notes || '';
                      if (dataSourceUrl && !baseNotes.includes('Link to view timeline:')) {
                        return `${baseNotes}\n\nLink to view timeline: ${dataSourceUrl}`;
                      }
                      return baseNotes;
                    })()}
                  </div>
                </div>
              )}
            </>
          )}

          {/* Notes when summary is not on this page: includes normal section pages and
              notes-only continuation pages (isSummaryOnlyPage && !showSummary — previously
              excluded by !isSummaryOnlyPage, so notes never rendered). */}
          {notes && !showSummaryOnCurrentPage && showNotesOnCurrentPage && (
            <div style={{
              position: 'absolute',
              top: `${notesTop}px`,
              left: '29px',
              right: '29px',
              fontSize: '12px',
              fontFamily: 'Inter, Arial, sans-serif',
              color: '#000000',
              lineHeight: '1.4'
            }}>
              <div style={{ fontWeight: 'bold', marginBottom: '8px', color: '#722420' }}>
                Notes:
              </div>
              <div style={{ paddingLeft: '10px', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                {(() => {
                  const baseNotes = notes || '';
                  if (dataSourceUrl && !baseNotes.includes('Link to view timeline:')) {
                    return `${baseNotes}\n\nLink to view timeline: ${dataSourceUrl}`;
                  }
                  return baseNotes;
                })()}
              </div>
            </div>
          )}

          {/* Payment Method */}



          {/* Frame Border */}
          <div className={styles.frame}>
            <div className={styles.rectangle} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default Step5Part2;